import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { QuotesFull, Leads, Tasks, Expenses, Reports } from '../services/api';
import { useAuth, ROLE_LABELS } from '../context/AuthContext';

const hour = new Date().getHours();
const greeting = hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : 'evening';
const firstOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10);
const today = new Date().toISOString().slice(0, 10);

export default function Dashboard() {
  const { user }  = useAuth();
  const navigate  = useNavigate();
  const [data, setData]   = useState(null);
  const [busy, setBusy]   = useState(true);
  const [top, setTop]     = useState(null); // { topLocation, topRep } or null if unavailable

  useEffect(() => {
    Promise.all([
      QuotesFull.list(),
      QuotesFull.invoices(),
      Leads.list(),
      Tasks.list(),
      Expenses.list(),
    ]).then(([quotes, invoices, leads, tasks, expenses]) => {
      setData({
        openQuotes  : quotes.filter(q => q.invoiced == 0).length,
        pendingInv  : invoices.filter(i => !i.ac_id).length,
        activeLeads : leads.filter(l => !['7','8','3','4'].includes(String(l.lstatus))).length,
        openTasks   : tasks.filter(t => !['done','cancelled'].includes(t.status)).length,
        pendingExp  : expenses.filter(e => !e.fin_id).length,
        recentQuotes: quotes.slice(0, 6),
        recentLeads : leads.slice(0, 6),
      });
    }).catch(console.error).finally(() => setBusy(false));

    // Managers/admins only — reps don't need a leaderboard staring at them daily
    if (['1', 1].includes(user?.ryts)) {
      Reports.byLocation(firstOfMonth, today)
        .then(r => {
          const topLocation = (r?.locations || [])[0] || null;
          const topRep = (r?.reps || [])[0] || null;
          if (topLocation || topRep) setTop({ topLocation, topRep });
        })
        .catch(() => setTop(null));
    }
  }, [user?.ryts]);

  const kpis = [
    { icon: '📋', label: 'Open Quotes',      val: data?.openQuotes,  to: '/quotes',   color: 'var(--accent)' },
    { icon: '🧾', label: 'Pending Invoices', val: data?.pendingInv,  to: '/invoices', color: 'var(--yellow)' },
    { icon: '🎯', label: 'Active Leads',     val: data?.activeLeads, to: '/leads',    color: 'var(--green)' },
    { icon: '✅', label: 'Open Tasks',       val: data?.openTasks,   to: '/tasks',    color: 'var(--purple)' },
    { icon: '💰', label: 'Pending Expenses', val: data?.pendingExp,  to: '/expenses', color: 'var(--red)' },
  ];

  if (busy) return <div className="spinner-wrap"><div className="spinner" /></div>;

  return (
    <div className="page">
      {/* Welcome */}
      <div className="flex-between page-header">
        <div>
          <div className="page-title">Good {greeting}, {user?.name?.split(' ')[0]} 👋</div>
          <div style={{ color: 'var(--muted)', fontSize: '.85rem' }}>{ROLE_LABELS[user?.ryts]} · {user?.business}</div>
        </div>
        <button className="btn btn-primary" onClick={() => navigate('/quotes/new')}>+ New Quote</button>
      </div>

      {/* KPIs */}
      <div className="kpi-grid">
        {kpis.map(k => (
          <div key={k.label} className="kpi-card" style={{ borderTopColor: k.color }} onClick={() => navigate(k.to)}>
            <div style={{ fontSize: '1.4rem' }}>{k.icon}</div>
            <div className="kpi-val" style={{ color: k.color }}>{k.val ?? '—'}</div>
            <div className="kpi-lbl">{k.label}</div>
          </div>
        ))}
      </div>

      {/* Top Performers — managers/admins only, this month */}
      {top && (top.topLocation || top.topRep) && (
        <div className="card" style={{ marginBottom: '1.2rem', cursor: 'pointer' }} onClick={() => navigate('/reports')}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '.9rem' }}>
            <div style={{ fontWeight: 600 }}>🏆 Top Performers This Month</div>
            <span style={{ color: 'var(--accent)', fontSize: '.8rem' }}>View full report →</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: '1rem' }}>
            {top.topLocation && (
              <div style={{ background: 'var(--bg)', borderRadius: 8, padding: '.8rem 1rem' }}>
                <div style={{ fontSize: '.75rem', color: 'var(--faint)' }}>Top Location</div>
                <div style={{ fontWeight: 700 }}>{top.topLocation.location_name}</div>
                <div style={{ fontSize: '.82rem', color: 'var(--muted)' }}>
                  ₹{top.topLocation.revenue.toLocaleString()} · {top.topLocation.quotes} quotes · {top.topLocation.win_rate}% win rate
                </div>
              </div>
            )}
            {top.topRep && (
              <div style={{ background: 'var(--bg)', borderRadius: 8, padding: '.8rem 1rem' }}>
                <div style={{ fontSize: '.75rem', color: 'var(--faint)' }}>Top Rep</div>
                <div style={{ fontWeight: 700 }}>{top.topRep.name}</div>
                <div style={{ fontSize: '.82rem', color: 'var(--muted)' }}>
                  ₹{top.topRep.revenue.toLocaleString()} · {top.topRep.quotes} quotes
                  {top.topRep.location_name ? ` · ${top.topRep.location_name}` : ''}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Recent tables */}
      <div className="two-col">
        <MiniTable title="Recent Quotes" rows={data?.recentQuotes}
          cols={[{ k:'name', l:'Quote' }, { k:'customer', l:'Cust' }, { k:'date_entered', l:'Date', f: v => v?.slice(0,10) }]}
          onRow={r => navigate(`/quotes/${r.id}`)} />
        <MiniTable title="Recent Leads" rows={data?.recentLeads}
          cols={[{ k:'company', l:'Company' }, { k:'name', l:'Contact' }, { k:'lstatus', l:'Status' }]}
          onRow={() => navigate('/leads')} />
      </div>

      {/* Quick actions */}
      <div className="card">
        <div style={{ fontWeight: 600, marginBottom: '.8rem' }}>Quick Actions</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: '.6rem' }}>
          {[
            ['📷 Scan Card', '/ocr'],
            ['📞 Log Call',  '/calls'],
            ['💰 Expense',   '/expenses'],
            ['✅ Task',      '/tasks'],
          ].map(([l, to]) => (
            <button key={l} className="btn btn-ghost" style={{ textAlign: 'left', fontSize: '.88rem' }}
              onClick={() => navigate(to)}>{l}</button>
          ))}
        </div>
      </div>
    </div>
  );
}

function MiniTable({ title, rows = [], cols, onRow }) {
  return (
    <div className="card">
      <div style={{ fontWeight: 600, marginBottom: '.8rem' }}>{title}</div>
      {rows.length === 0
        ? <div className="empty" style={{ padding: '.5rem' }}>No records</div>
        : <table className="tbl">
            <thead><tr>{cols.map(c => <th key={c.k}>{c.l}</th>)}</tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="clickable" onClick={() => onRow(r)}>
                  {cols.map(c => <td key={c.k}>{c.f ? c.f(r[c.k]) : (r[c.k] ?? '—')}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
      }
    </div>
  );
}
